import { useHistory } from '@framework/ui/core/router/TypedRoute.tsx';
import { useCallback, useEffect, useRef } from 'react';
import { type ISubmissionMenuItem } from '../submission/menu/ISubmissionMenuItem.tsx';

export type NavigationDirection = 'next' | 'previous';

/**
 * Ritorna la funzione che permette, attraverso il cambio di URL
 * di spostarsi tra i moduli della richiesta.
 */
export function useSubmissionNavigation(params: {
  submissionId: string | null | undefined;
  steps: ISubmissionMenuItem[];
}) {
  const { submissionId, steps } = params;

  const history = useHistory();

  const stepsRef = useRef(steps);

  const navigate = useCallback(
    (currentStep: string, direction: NavigationDirection) => {
      if (!submissionId) return;
      const localSteps = stepsRef.current;

      const currentStepIndex = localSteps.findIndex(s => s.key === currentStep);
      const nextStep =
        direction === 'next'
          ? localSteps[currentStepIndex + 1]
          : localSteps[currentStepIndex - 1];

      if (nextStep?.to != null) {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        history.push(nextStep.to);
      }
    },
    [history, submissionId],
  );

  const canNext = useCallback(
    (currentStep: Maybe<string>) => {
      if (!submissionId) return false;

      const currentStepIndex = steps.findIndex(s => s.key === currentStep);
      return currentStepIndex < steps.length - 1;
    },
    [steps, submissionId],
  );

  const canPrevious = useCallback(
    (currentStep: Maybe<string>) => {
      if (!submissionId) return false;

      const currentStepIndex = steps.findIndex(s => s.key === currentStep);
      return currentStepIndex > 0;
    },
    [steps, submissionId],
  );

  useEffect(() => {
    stepsRef.current = steps;
  }, [steps]);

  return { navigate, canNext, canPrevious };
}

export type ISubmissionNavigator = ReturnType<typeof useSubmissionNavigation>;
